import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Save, CheckCircle, ClipboardList, Calendar, FileCheck, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import AppHeader from '../components/AppHeader';

const enconOrange = '#ee7203';

const AdjustmentReport = ({ user }) => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  
  const [report, setReport] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState('');
  const [completing, setCompleting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  useEffect(() => {
    loadReport();
  }, [reportId]);
  
  const loadReport = async () => {
    try {
      setLoading(true);
      const response = await api.get(`${API}/pi-counts/adjustment-reports/${reportId}`);
      setReport(response.data.report);
      setItems(response.data.report?.items || []);
    } catch (error) {
      console.error('Error loading adjustment report:', error);
      toast.error('Failed to load adjustment report');
    } finally {
      setLoading(false);
    }
  };
  
  const handleNotesChange = (itemId, notes) => {
    setItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, notes } : item
    ));
  };
  
  const saveNotes = async (itemId, notes) => {
    try {
      setSaving(true);
      await api.put(`${API}/pi-counts/adjustment-reports/${reportId}/item/${itemId}/notes`, {
        notes
      });
      toast.success('Notes saved');
    } catch (error) {
      console.error('Error saving notes:', error);
      toast.error('Failed to save notes');
    } finally {
      setSaving(false);
    }
  };
  
  const completeReport = async () => {
    if (!referenceNumber.trim()) {
      toast.error('Please enter a reference number');
      return;
    }
    
    try {
      setCompleting(true);
      await api.put(`${API}/pi-counts/adjustment-reports/${reportId}/complete`, {
        reference_number: referenceNumber.trim()
      });
      toast.success('Adjustment report completed');
      setShowCompleteModal(false);
      navigate('/pi-counts');
    } catch (error) {
      console.error('Error completing report:', error);
      toast.error('Failed to complete report');
    } finally {
      setCompleting(false);
    }
  };
  
  const deleteReport = async () => {
    if (!window.confirm('Are you sure you want to delete this adjustment report? This cannot be undone.')) return;
    try {
      setDeleting(true);
      await api.delete(`${API}/pi-counts/adjustment-reports/${reportId}`);
      toast.success('Adjustment report deleted');
      navigate('/pi-counts');
    } catch (error) {
      console.error('Error deleting report:', error);
      toast.error('Failed to delete adjustment report');
    } finally {
      setDeleting(false);
    }
  };
  
  const subHeaderLeft = (
    <>
      <button
        onClick={() => navigate('/pi-counts')}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold bg-white/90 hover:bg-white transition-colors"
        style={{ color: enconOrange }}
        data-testid="adjustment-report-back-btn"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Back</span>
      </button>
      <div className="flex items-center gap-1.5 rounded-full px-3 py-1 flex-shrink-0" style={{ background: 'rgba(0,0,0,0.22)' }}>
        <ClipboardList className="w-3.5 h-3.5 text-white/90 flex-shrink-0" />
        <span className="text-white font-bold text-xs sm:text-sm whitespace-nowrap">Adjustment Report</span>
      </div>
    </>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <AppHeader user={user} subHeaderLeft={subHeaderLeft} />
        <div className="flex items-center justify-center h-screen">
          <p className="text-lg text-gray-600">Loading adjustment report...</p>
        </div>
      </div>
    );
  }
  
  if (!report) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <AppHeader user={user} subHeaderLeft={subHeaderLeft} />
        <div className="flex items-center justify-center h-screen">
          <p className="text-lg text-red-600">Adjustment report not found</p>
        </div>
      </div>
    );
  }
  
  const isCompleted = report.status === 'completed';
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <AppHeader user={user} subHeaderLeft={subHeaderLeft} />
      
      <div className="px-4 lg:px-8 py-6" style={{ paddingTop: '164px' }}>
        {/* Report Header */}
        <Card className="mb-6 bg-white border-2 border-blue-200">
          <CardHeader className="bg-blue-50">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl flex items-center gap-2 text-blue-700">
                  <ClipboardList className="w-7 h-7" />
                  Adjustment Report
                </CardTitle>
                <p className="text-lg font-semibold mt-2">{report.count_name}</p>
                <div className="flex items-center gap-2 mt-2 text-gray-600">
                  <Calendar className="w-4 h-4" />
                  <span className="text-sm">
                    Count Date: {report.count_date && format(new Date(report.count_date), 'dd MMMM yyyy')}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Created: {report.created_at && format(new Date(report.created_at), 'dd/MM/yyyy HH:mm')}
                </p>
              </div>
              <div className="text-right space-y-2">
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  isCompleted ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {isCompleted ? 'Completed' : 'Pending'}
                </span>
                {isCompleted && (
                  <p className="text-sm text-green-700 mt-2 font-semibold">
                    Ref: {report.reference_number}
                  </p>
                )}
                <div className="mt-2">
                  <Button
                    onClick={deleteReport}
                    variant="outline"
                    size="sm"
                    className="rounded-full border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                    disabled={deleting}
                    data-testid="delete-adjustment-btn"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1" />
                    {deleting ? 'Deleting...' : 'Delete'}
                  </Button>
                </div>
              </div>
            </div>
          </CardHeader>
        </Card>
        
        {/* Action Buttons */}
        {!isCompleted && (
          <div className="flex gap-3 mb-6">
            <Button
              onClick={() => setShowCompleteModal(true)}
              style={{ backgroundColor: '#22c55e', color: 'white' }}
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Complete Adjustments
            </Button>
          </div>
        )}
        
        {/* Items List */}
        <Card className="bg-white">
          <CardHeader>
            <CardTitle className="text-lg text-blue-700">
              Adjustment Items ({items.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-blue-50 border-b-2 border-blue-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Product Code</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Description</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Expected</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Counted</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">On Road</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Discrepancy</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(items) && items.map((item, index) => {
                    // Defensive check: ensure item is an object with expected properties
                    if (!item || typeof item !== 'object') return null;
                    
                    const itemId = item.id || item.product_id || `item-${index}`;
                    const productCode = String(item.product_code || item.code || 'Unknown');
                    const productName = String(item.product_name || item.description || '');
                    const expectedQty = typeof item.expected_qty === 'number' ? item.expected_qty : 0;
                    const countedQty = typeof item.counted_qty === 'number' ? item.counted_qty : 0;
                    const onTheRoad = typeof item.on_the_road === 'number' ? item.on_the_road : 0;
                    const discrepancy = typeof item.discrepancy === 'number' ? item.discrepancy : 0;
                    
                    return (
                      <tr key={itemId} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium">{productCode}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                          {productName || 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-sm text-right">
                          {expectedQty.toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm text-right">
                          {countedQty.toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm text-right">
                          {onTheRoad.toFixed(2)}
                        </td>
                        <td className={`px-4 py-3 text-sm text-right font-semibold ${
                          discrepancy < 0 ? 'text-red-600' : discrepancy > 0 ? 'text-orange-600' : 'text-green-600'
                        }`}>
                          {discrepancy >= 0 ? '+' : ''}{discrepancy.toFixed(2)}
                        </td>
                        <td className="px-4 py-3">
                          {isCompleted ? (
                            <p className="text-sm text-gray-600">{item.notes || '-'}</p>
                          ) : (
                            <div className="flex gap-2">
                              <Textarea
                                value={item.notes || ''}
                                onChange={(e) => handleNotesChange(itemId, e.target.value)}
                                placeholder="Add notes..."
                                className="text-sm min-h-[60px] w-48"
                              />
                              <Button
                                onClick={() => saveNotes(itemId, item.notes || '')}
                                disabled={saving}
                                size="sm"
                                variant="outline"
                                style={{ borderColor: enconOrange, color: enconOrange }}
                              >
                                <Save className="w-3 h-3" />
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Complete Modal */}
      <Dialog open={showCompleteModal} onOpenChange={setShowCompleteModal}>
        <DialogContent className="max-w-md rounded-2xl" style={{ border: '3px solid #22c55e' }}>
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-green-600 flex items-center gap-2">
              <FileCheck className="w-6 h-6" />
              Complete Adjustment Report
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Enter the adjustment reference number to complete this report.
            </p>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Reference Number</label>
              <Input
                value={referenceNumber}
                onChange={(e) => setReferenceNumber(e.target.value)}
                placeholder="e.g., ADJ-2024-001"
                className="mt-1"
              />
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button
                onClick={() => setShowCompleteModal(false)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={completeReport}
                disabled={completing || !referenceNumber.trim()}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              >
                {completing ? 'Completing...' : 'Complete'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdjustmentReport;
